import { CirclePause } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PatientInfo } from 'widgets/home/PatientInfo';
import { ConsultationTitle } from 'widgets/home/PatientInfo.styles';
import {
  formatElapsedTime,
  formatReadableDate,
  formatReadableTime,
  formatRelativeDaySuffix,
} from 'utils';
import type { PausedConsultation } from 'types/pausedConsultation';
import { PausedConsultationActions } from './PausedConsultationActions';
import {
  ActionsBar,
  CardContent,
  PausedBadge,
  StyledCard,
  Topbar,
  TopbarInfo,
} from './PausedConsultationCard.styles';

interface PausedConsultationCardProps {
  consultation: PausedConsultation;
  showPatient?: boolean;
}

export const PausedConsultationCard = ({
  consultation,
  showPatient = true,
}: PausedConsultationCardProps) => {
  const { t } = useTranslation();
  const { fullname, birthdate } = consultation.patient;
  const formattedDate = formatReadableDate(consultation.startedAt);
  const relativeSuffix = formatRelativeDaySuffix(consultation.startedAt);
  const formattedTime = formatReadableTime(consultation.startedAt);
  const elapsed = formatElapsedTime(consultation.totalDurationSeconds);
  const pausedAtLabel = t('recording.pausedConsultation.pausedAt', { duration: elapsed });
  const consultationHeading = t('patientOverview.pastConsultations.heading', {
    date: formattedDate,
  });

  return (
    <StyledCard
      role="listitem"
      aria-label={showPatient ? fullname || t('home.unknownPatient') : consultationHeading}
    >
      <CardContent>
        <Topbar>
          <PausedBadge aria-hidden="true">
            <CirclePause className="size-3" />
          </PausedBadge>
          <TopbarInfo>{`${formattedDate}${relativeSuffix} · ${formattedTime} · ${pausedAtLabel}`}</TopbarInfo>
        </Topbar>
        {showPatient ? (
          <PatientInfo
            patientName={fullname}
            birthdate={birthdate}
            medatixxId={consultation.medatixxPatientId}
          />
        ) : (
          <ConsultationTitle>{`${consultationHeading}${relativeSuffix}`}</ConsultationTitle>
        )}
      </CardContent>
      <ActionsBar>
        <PausedConsultationActions consultation={consultation} />
      </ActionsBar>
    </StyledCard>
  );
};
